import { Checkbox } from "@fluentui/react";
import { FilterManager } from "@v2/components/controls/FilterManager/FilterManager";
import { FormControl } from "@v2/components/controls/FormControl/FormControl";
import HSObjectPicker from "@v2/components/controls/implementations/HSObjectPicker";
import { AdvancedSettings } from "@v2/components/flowsteps/editors/common/AdvancedSettings/AdvancedSettings";
import { FlowstepButtons } from "@v2/components/flowsteps/editors/common/FlowstepButtons/FlowstepButtons";
import { FlowstepContainer } from "@v2/components/flowsteps/editors/common/FlowstepContainer/FlowstepContainer";
import { PullOptions } from "@v2/components/flowsteps/editors/common/PullOptions/PullOptions";
import { PullHubspotListState } from "@v2/components/flowsteps/editors/hubspot/list/PullHubspotList/pull-hubspot-list-state";
import { FieldPicker } from "@v2/components/flowsteps/editors/PullOjbectHS/FieldPicker/FieldPicker";
import { getOperatorsForType } from "@v2/components/flowsteps/editors/PullOjbectHS/filters/hs-operators";
import { PropertyEdit } from "@v2/components/flowsteps/editors/PullOjbectHS/PropertyEdit/PropertyEdit";
import TitledPage from "@v2/components/layout/TitledPage";
import { observer } from "mobx-react";
import React, { useEffect, useState } from 'react';
import { FilterCondition, PullMode } from "shared/types/flowstep";
import GroupPane from "src/components/base/GroupPane";
import { ModalPage } from "src/components/base/Page";
import SearchableComboBox from "src/components/base/SearchableComboBox";
import { officeStore } from 'src/stores';
import { useStyles } from './PullHubspotList.styles';
import UnsavedChangesDialog from "@v2/hooks/UnsavedChangesDialog";
import { renderEditorForTypeAndOperator } from "src/v2/components/controls/editors/hs-editors";

interface PullHubspotListProps {
  id?: string;
  createNew?: boolean;
}

export const PullHubspotList: React.FC<PullHubspotListProps> = observer(({ id, createNew }) => {
  const [state] = useState(() => new PullHubspotListState());
  const [isFilterManagerOpen, setFilterManagerOpen] = useState(false);
  const [optionsCollapsed, setOptionsCollapsed] = useState(true);
  const [advancedCollapsed, setAdvancedCollapsed] = useState(true);
  const styles = useStyles();

  useEffect(() => {
    (async () => {
      await state.loadFlowstep(id, createNew);
    })();
  }, [id, createNew]);

  const handleFilterApply = (conditions: FilterCondition[]) => {
    state.pullSettings.conditions = conditions;
    setFilterManagerOpen(false);
  };

  let sectionNumber = 1;

  const title = `${state.isNewFlowstep ? 'New: ' : ''}From ${state.pullSettings.selectedObject?.label || 'a List'}`;

  return (<>
    <UnsavedChangesDialog
      onSave={state.saveFlowstep}
      isNotSaved={() => state.isNotSaved}
    />
    <TitledPage title={title}>
      <FlowstepContainer>
        <PropertyEdit
          label="Create new worksheet"
          tooltip={"We'll create a new worksheet for you and pull the data there"}
        >
          <Checkbox
            checked={state.pullSettings.createNewSheet}
            onChange={(_, checked) => state.pullSettings.createNewSheet = !!checked}
          />
        </PropertyEdit>

        <FormControl label={`${sectionNumber++}. Select the list to get data from`}>
          <HSObjectPicker
            data={state.pullSettings.objectLists.data}
            value={state.pullSettings.selectedObject}
            onChange={(_, newValue) => state.pullSettings.selectedObject = newValue}
            disabled={state.pullSettings.objectLists.isLoading}
            errorMessage={state.validationErrors.get('object-type-not-selected')}
            placeholder='Select list'
          />
        </FormControl>

        <FormControl label={`${sectionNumber++}. Select fields to return`}>
          <FieldPicker
            items={state.pullSettings.objectFields.data}
            selectedFieldNames={state.pullSettings.selectedFieldNames}
            onChange={(value) => state.pullSettings.selectedFieldNames = value}
            disabled={!state.pullSettings.objectFields.isReady}
          />
        </FormControl>

        <ModalPage
          isOpen={isFilterManagerOpen}
          title="Filter manager"
          onClose={() => setFilterManagerOpen(false)}
          helpLink={undefined}
          component={() => (
            <FilterManager
              objectFields={state.pullSettings.objectFields.data}
              conditions={state.pullSettings.conditions}
              getFieldOperators={getOperatorsForType}
              getFieldEditor={renderEditorForTypeAndOperator}
              onApply={handleFilterApply}
            />
          )}
        />

        <FormControl label={`${sectionNumber++}. Select pull mode`}>
          <SearchableComboBox
            options={[
              { key: PullMode.APPEND, text: 'Append' },
              { key: PullMode.NEW_SHEET, text: 'New Sheet' },
              { key: PullMode.OVERWRITE, text: 'Overwrite' },
            ]}
            selectedKey={state.pullSettings.pullMode}
            onChange={(_, selectedItem) => state.pullSettings.pullMode = selectedItem.key}
            placeholder="Pull mode option"
          />
          <div className={styles.fieldDescription}>
            On refresh, overwrites the data in the range where it was initially pulled.
          </div>
        </FormControl>

        <GroupPane
          title="Options"
          collapsed={optionsCollapsed}
          onCollapsed={(collapsed: boolean) => setOptionsCollapsed(collapsed)}
          tooltip=""
          className={styles.groupPane}
        >
          <PullOptions state={state.pullOptions} />
          <PropertyEdit
            label="Force formula recalculation when done"
            tooltip={'Recalculates all workbook formulas and pivot tables after execution of this flow step'}
          >
            <Checkbox
              checked={state.recalculateFormulas}
              onChange={(_, checked) => state.recalculateFormulas = !!checked}
            />
          </PropertyEdit>
        </GroupPane>

        <GroupPane
          title="Advanced"
          collapsed={advancedCollapsed}
          onCollapsed={(collapsed: boolean) => setAdvancedCollapsed(collapsed)}
          tooltip=""
          className={styles.groupPane}
        >
          <AdvancedSettings
            baseEditorState={state}
            namePlaceholder={state.generatedFlowstepName}
            timestampCellProps={{
              allowNewSheet: true,
              getTimestampSheetId: state.timestampSheetId ?? officeStore.activeWorksheetId,
            }}
          />
        </GroupPane>

        <FlowstepButtons
          editorState={state}
          disabled={state.isFlowstepButtonsDisabled}
        />
      </FlowstepContainer>
    </TitledPage>
  </>)
});
